import { calculateDaysRemaining } from '@/pages/umeds/order-medication/util';
import type { IntervalDetails } from '@montugroup/circuit-shared/types/refill';
import { Alert, AlertTitle, Link, Typography, ViewObserver } from '@montugroup/design-system';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LockClock from '@mui/icons-material/LockClock';
import { styled } from '@mui/material/styles';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { DispenseIntervalDialog } from './DispenseIntervalDialog';

interface DispenseIntervalAlertProps {
  reorderDate?: string;
  intervalStatus?: IntervalDetails['estimatedIntervalState'];
  productId?: string | number;
}

const StyledLinkInfo = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '&&': {
    textDecoration: 'underline',
    color: theme.palette.info.main,
  },
  cursor: 'pointer',
}));

const StyledLinkError = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '&&': {
    textDecoration: 'underline',
    color: theme.palette.error.main,
  },
  cursor: 'pointer',
}));

export const DispenseIntervalAlert: React.FC<DispenseIntervalAlertProps> = ({
  reorderDate,
  intervalStatus,
  productId,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleLearnMoreClick = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const daysRemaining = reorderDate ? calculateDaysRemaining(reorderDate) : 0;

  const renderAlert = (): ReactNode => {
    // LOCKED state - always show in the can't order now section
    if (intervalStatus === 'LOCKED') {
      return (
        <ViewObserver id={`dispense-interval-alert-locked-${productId ?? ''}`}>
          <Alert severity="error" icon={<LockClock />}>
            <AlertTitle>You&apos;ve hit your maximum units</AlertTitle>
            <StyledLinkError
              variant="body2"
              fontWeight="fontWeightMedium"
              onClick={handleLearnMoreClick}
              id={`dispense-interval-alert-locked-link-${productId ?? ''}`}
            >
              Learn more about Dispense Interval <InfoOutlined fontSize="small" sx={{ ml: 0.5 }} />
            </StyledLinkError>
          </Alert>
        </ViewObserver>
      );
    }

    // PRE_ORDER state - show on the prescribed medication section
    if (intervalStatus === 'PRE_ORDER') {
      return (
        <ViewObserver id={`dispense-interval-alert-variantA-${productId ?? ''}`}>
          <Alert severity="info" icon={<LockClock />}>
            {reorderDate && (
              <>
                <AlertTitle>
                  Order will process after {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}
                </AlertTitle>
                <Typography variant="body2">
                  To support safe use, we will process this order on or after <strong>{reorderDate}</strong>.
                </Typography>
              </>
            )}
            <StyledLinkInfo
              variant="body2"
              fontWeight="fontWeightMedium"
              onClick={handleLearnMoreClick}
              id={`dispense-interval-alert-variantA-link-${productId ?? ''}`}
            >
              Learn more about Dispense Interval <InfoOutlined fontSize="small" sx={{ ml: 0.5 }} />
            </StyledLinkInfo>
          </Alert>
        </ViewObserver>
      );
    }
  };

  return (
    <>
      {renderAlert()}
      <DispenseIntervalDialog dialogOpen={dialogOpen} handleCloseDialog={handleCloseDialog} />
    </>
  );
};
